import React, { useState, useEffect, useRef } from 'react';
import { t } from '../lib/i18n';
import { getAgentConfig, updateAgentConfig } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Brain, Heart, MessageCircle, Plus, X, Save, User, Phone, Mail,
  Mic, Volume2, Image, Shield, Sparkles, Eye
} from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const VOICES = [
  { id: 'alloy', name: 'Alloy', desc: 'Neutralny, wszechstronny', gender: 'neutral' },
  { id: 'echo', name: 'Echo', desc: 'Ciepły, męski', gender: 'male' },
  { id: 'fable', name: 'Fable', desc: 'Narracyjny, spokojny', gender: 'neutral' },
  { id: 'onyx', name: 'Onyx', desc: 'Niski, autorytarny', gender: 'male' },
  { id: 'nova', name: 'Nova', desc: 'Energiczny, przyjazny', gender: 'female' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Delikatny, profesjonalny', gender: 'female' },
];

export default function AgentConfig({ lang, tenantId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewVoice, setPreviewVoice] = useState(null);

  useEffect(() => { loadConfig(); }, [tenantId]);

  async function loadConfig() {
    setLoading(true);
    try {
      const c = await getAgentConfig(tenantId);
      setConfig(c);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await updateAgentConfig({
        identity: config.identity,
        beliefs: config.beliefs,
        roles: config.roles,
        tasks: config.tasks,
        language: config.language
      }, tenantId);
      setConfig(updated);
      toast.success(lang === 'pl' ? 'Konfiguracja zapisana' : 'Configuration saved');
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd zapisu' : 'Save error');
    }
    setSaving(false);
  }

  function updateIdentity(field, value) {
    setConfig(prev => ({
      ...prev,
      identity: { ...prev.identity, [field]: value }
    }));
  }

  function addBelief() {
    setConfig(prev => ({
      ...prev,
      beliefs: [...(prev.beliefs || []), '']
    }));
  }

  function updateBelief(index, value) {
    setConfig(prev => ({
      ...prev,
      beliefs: prev.beliefs.map((b, i) => i === index ? value : b)
    }));
  }

  function removeBelief(index) {
    setConfig(prev => ({
      ...prev,
      beliefs: prev.beliefs.filter((_, i) => i !== index)
    }));
  }

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }

  if (!config) return null;

  const identity = config.identity || {};

  return (
    <div className="space-y-6" data-testid="agent-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Profil Agenta' : 'Agent Profile'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl' ? 'Konfiguruj tożsamość, dane kontaktowe, głos i przekonania agenta' : 'Configure agent identity, contact info, voice and beliefs'}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-agent-config">
          <Save className="w-4 h-4" />
          {saving ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : t('save', lang)}
        </Button>
      </div>

      {/* Agent Card — visual summary */}
      <Card className="border border-border overflow-hidden">
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
          <div className="flex items-center gap-5">
            {/* Avatar */}
            <div className="relative group">
              <div className="w-20 h-20 rounded-2xl bg-primary/20 border-2 border-primary/30 flex items-center justify-center text-primary overflow-hidden">
                {identity.avatar_url ? (
                  <img src={identity.avatar_url} alt={identity.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10" />
                )}
              </div>
              <div className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Image className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{identity.name || 'Adam'}</h2>
              <p className="text-sm text-muted-foreground">{identity.bio || identity.experience}</p>
              <div className="flex items-center gap-4 mt-2">
                {identity.email && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {identity.email}
                  </span>
                )}
                {identity.phone && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {identity.phone}
                  </span>
                )}
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mic className="w-3 h-3" /> {VOICES.find(v => v.id === (identity.voice_id || 'onyx'))?.name || 'Onyx'}
                </span>
              </div>
            </div>
            <Badge variant="outline" className="text-xs gap-1">
              <Shield className="w-3 h-3" />
              {lang === 'pl' ? 'Agent AI' : 'AI Agent'}
            </Badge>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="identity" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="identity" className="gap-1.5">
            <User className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Tożsamość' : 'Identity'}
          </TabsTrigger>
          <TabsTrigger value="contact" className="gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Dane kontaktowe' : 'Contact'}
          </TabsTrigger>
          <TabsTrigger value="voice" className="gap-1.5">
            <Mic className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Głos' : 'Voice'}
          </TabsTrigger>
          <TabsTrigger value="personality" className="gap-1.5">
            <Brain className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Osobowość' : 'Personality'}
          </TabsTrigger>
          <TabsTrigger value="beliefs" className="gap-1.5">
            <Heart className="w-3.5 h-3.5" />
            {t('beliefs', lang)}
          </TabsTrigger>
          <TabsTrigger value="communication" className="gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" />
            {lang === 'pl' ? 'Komunikacja' : 'Communication'}
          </TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{lang === 'pl' ? 'Podstawowe dane' : 'Basic Info'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t('agentName', lang)} *</Label>
                  <Input
                    value={identity.name || ''}
                    onChange={e => updateIdentity('name', e.target.value)}
                    data-testid="agent-name-input"
                    className="mt-1"
                    placeholder="Adam"
                  />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Bio / opis' : 'Bio / description'}</Label>
                  <Input
                    value={identity.bio || ''}
                    onChange={e => updateIdentity('bio', e.target.value)}
                    className="mt-1"
                    placeholder={lang === 'pl' ? 'Krótki opis agenta...' : 'Short agent description...'}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('experience', lang)}</Label>
                <Input
                  value={identity.experience || ''}
                  onChange={e => updateIdentity('experience', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">{t('origin', lang)}</Label>
                <Input
                  value={identity.origin || ''}
                  onChange={e => updateIdentity('origin', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'URL awatara' : 'Avatar URL'}</Label>
                <Input
                  value={identity.avatar_url || ''}
                  onChange={e => updateIdentity('avatar_url', e.target.value)}
                  className="mt-1"
                  placeholder="https://..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {lang === 'pl' ? 'Link do zdjęcia profilowego agenta' : 'Link to agent profile picture'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{lang === 'pl' ? 'Dane kontaktowe agenta' : 'Agent Contact Info'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> Email
                  </Label>
                  <Input
                    type="email"
                    value={identity.email || ''}
                    onChange={e => updateIdentity('email', e.target.value)}
                    className="mt-1"
                    placeholder="adam@firma.pl"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'pl' ? 'Adres email z którego agent wysyła wiadomości' : 'Email address agent sends from'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    <Phone className="w-3 h-3" /> {t('phone', lang)}
                  </Label>
                  <Input
                    type="tel"
                    value={identity.phone || ''}
                    onChange={e => updateIdentity('phone', e.target.value)}
                    className="mt-1"
                    placeholder="+48 123 456 789"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'pl' ? 'Numer telefonu agenta (integracja z Twilio)' : 'Agent phone number (Twilio integration)'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Tab */}
        <TabsContent value="voice" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Volume2 className="w-4 h-4" />
                {lang === 'pl' ? 'Wybór głosu' : 'Voice Selection'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {lang === 'pl'
                  ? 'Wybierz głos którym agent będzie mówił podczas rozmów telefonicznych (OpenAI TTS).'
                  : 'Choose the voice the agent will use during phone calls (OpenAI TTS).'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {VOICES.map(voice => {
                  const isSelected = (identity.voice_id || 'onyx') === voice.id;
                  return (
                    <button
                      key={voice.id}
                      onClick={() => updateIdentity('voice_id', voice.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-border hover:border-border/80 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{voice.name}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {voice.gender === 'male' ? (lang === 'pl' ? 'Męski' : 'Male')
                            : voice.gender === 'female' ? (lang === 'pl' ? 'Żeński' : 'Female')
                            : (lang === 'pl' ? 'Neutralny' : 'Neutral')}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{voice.desc}</p>
                      {isSelected && (
                        <Badge className="mt-2 text-[10px]">
                          {lang === 'pl' ? 'Wybrany' : 'Selected'}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="pt-2">
                <Label className="text-xs">{lang === 'pl' ? 'Prędkość mowy' : 'Speech speed'}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={identity.voice_speed || 1.0}
                    onChange={e => updateIdentity('voice_speed', parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-mono w-10 text-right">{(identity.voice_speed || 1.0).toFixed(1)}x</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Personality Tab */}
        <TabsContent value="personality" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{lang === 'pl' ? 'Cechy osobowości' : 'Personality Traits'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">{t('defaultStyle', lang)}</Label>
                <Input
                  value={identity.default_style || ''}
                  onChange={e => updateIdentity('default_style', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> {t('superpower', lang)}
                </Label>
                <Input
                  value={identity.superpower || ''}
                  onChange={e => updateIdentity('superpower', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> {t('weakness', lang)}
                </Label>
                <Input
                  value={identity.weakness || ''}
                  onChange={e => updateIdentity('weakness', e.target.value)}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Beliefs Tab */}
        <TabsContent value="beliefs" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">
                {t('beliefs', lang)}
                <span className="text-muted-foreground font-normal ml-2">({config.beliefs?.length || 0})</span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={addBelief} className="gap-1" data-testid="add-belief-button">
                <Plus className="w-3.5 h-3.5" />
                {t('addBelief', lang)}
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {(config.beliefs || []).map((belief, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs text-muted-foreground mt-2.5 w-5 text-right flex-shrink-0">{i + 1}.</span>
                  <Textarea
                    value={belief}
                    onChange={e => updateBelief(i, e.target.value)}
                    className="min-h-[40px] text-sm resize-none"
                    rows={1}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBelief(i)}
                    className="flex-shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label="Remove belief"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communication Tab */}
        <TabsContent value="communication" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{lang === 'pl' ? 'Ustawienia komunikacji' : 'Communication Settings'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">{t('language', lang)}</Label>
                <Select
                  value={config.language || 'pl'}
                  onValueChange={v => setConfig(prev => ({ ...prev, language: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="agent-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pl">Polski</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="fr">Fran\u00e7ais</SelectItem>
                    <SelectItem value="es">Espa\u00f1ol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
